import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Upload, X, Plus, Trash2, Play } from 'lucide-react';
import VisionBoardDisplay from './VisionBoardDisplay';

interface VisionBoard {
  id: string;
  title: string;
  description?: string;
  affirmations: string[];
  action_items: { text: string; completed: boolean }[];
  images: { url: string; caption?: string }[];
  layout_type: string;
  is_favorite: boolean;
}

interface VisionBoardCreatorProps {
  user: any;
}

export default function VisionBoardCreator({ user }: VisionBoardCreatorProps) {
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [affirmations, setAffirmations] = useState<string[]>(['']);
  const [actionItems, setActionItems] = useState<{ text: string; completed: boolean }[]>([{ text: '', completed: false }]);
  const [images, setImages] = useState<{ file?: File; url: string; caption?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [savedBoards, setSavedBoards] = useState<VisionBoard[]>([]);
  const [previewBoard, setPreviewBoard] = useState<VisionBoard | null>(null);

  useEffect(() => {
    loadVisionBoards();
  }, [user?.id]);

  const loadVisionBoards = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from('vision_boards')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading vision boards:', error);
      return;
    }

    const boards = (data || []).map(board => ({
      ...board,
      action_items: (board.action_items as any) || [],
      images: (board.images as any) || [],
    })) as VisionBoard[];

    setSavedBoards(boards);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    toast({
      title: 'Compressing images...',
      description: 'Optimising your images for faster uploads',
    });

    try {
      const { compressMultipleImages } = await import('@/utils/imageCompression');
      const compressed = await compressMultipleImages(files, 20);
      
      const hasErrors = compressed.some(c => c.error);
      if (hasErrors) {
        const errorFiles = compressed.filter(c => c.error).map(c => c.error);
        toast({
          title: 'Some images failed',
          description: errorFiles.join(', '),
          variant: 'destructive',
        });
      }

      const successfulImages = compressed
        .filter(c => !c.error)
        .map(({ file }) => ({
          file,
          url: URL.createObjectURL(file),
          caption: ''
        }));

      setImages(prev => [...prev, ...successfulImages]);
      
      const totalSavings = compressed.reduce(
        (acc, c) => acc + (c.originalSize - c.compressedSize),
        0
      ) / 1024 / 1024;

      toast({
        title: 'Images ready!',
        description: `${successfulImages.length} image(s) added (saved ${totalSavings.toFixed(1)}MB)`,
      });
    } catch (error) {
      console.error('Error processing images:', error);
      toast({
        title: 'Error',
        description: 'Failed to process images',
        variant: 'destructive',
      });
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      const newImages = [...prev];
      if (newImages[index].file) {
        URL.revokeObjectURL(newImages[index].url);
      }
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const updateImageCaption = (index: number, caption: string) => {
    setImages(prev => {
      const newImages = [...prev];
      newImages[index] = { ...newImages[index], caption };
      return newImages;
    });
  };

  const addAffirmation = () => {
    setAffirmations(prev => [...prev, '']);
  };

  const updateAffirmation = (index: number, value: string) => {
    setAffirmations(prev => {
      const newAffirmations = [...prev];
      newAffirmations[index] = value;
      return newAffirmations;
    });
  };

  const removeAffirmation = (index: number) => {
    setAffirmations(prev => prev.filter((_, i) => i !== index));
  };

  const addActionItem = () => {
    setActionItems(prev => [...prev, { text: '', completed: false }]);
  };

  const updateActionItem = (index: number, text: string) => {
    setActionItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], text };
      return newItems;
    });
  };

  const removeActionItem = (index: number) => {
    setActionItems(prev => prev.filter((_, i) => i !== index));
  };

  const handlePreview = () => {
    const board: VisionBoard = {
      id: 'preview',
      title: title || 'My Vision Board',
      description,
      affirmations: affirmations.filter(a => a.trim()),
      action_items: actionItems.filter(item => item.text.trim()),
      images: images.map(img => ({ url: img.url, caption: img.caption })),
      layout_type: 'grid',
      is_favorite: false
    };
    setPreviewBoard(board);
  };

  const saveVisionBoard = async () => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save a vision board',
        variant: 'destructive',
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: 'Title required',
        description: 'Please enter a title for your vision board',
        variant: 'destructive',
      });
      return;
    }

    if (images.length === 0) {
      toast({
        title: 'Images required',
        description: 'Please add at least one image to your vision board',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Upload images to storage
      const uploadedImages = await Promise.all(
        images.map(async (img) => {
          if (!img.file) return { url: img.url, caption: img.caption };

          const fileName = `${user.id}/${Date.now()}_${img.file.name}`;
          const { error: uploadError, data } = await supabase.storage
            .from('manifestation-media')
            .upload(fileName, img.file);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('manifestation-media')
            .getPublicUrl(fileName);

          return { url: publicUrl, caption: img.caption };
        })
      );

      // Save vision board to database
      const { error } = await supabase.from('vision_boards').insert({
        user_id: user.id,
        title,
        description,
        affirmations: affirmations.filter(a => a.trim()),
        action_items: actionItems.filter(item => item.text.trim()),
        images: uploadedImages,
        layout_type: 'grid',
      });

      if (error) throw error;

      toast({
        title: 'Success!',
        description: 'Your vision board has been saved',
      });

      // Reset form
      setTitle('');
      setDescription('');
      setAffirmations(['']);
      setActionItems([{ text: '', completed: false }]);
      setImages([]);
      loadVisionBoards();
    } catch (error) {
      console.error('Error saving vision board:', error);
      toast({
        title: 'Error',
        description: 'Failed to save vision board',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteBoard = async (boardId: string) => {
    const { error } = await supabase
      .from('vision_boards')
      .delete()
      .eq('id', boardId);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete vision board',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Deleted',
      description: 'Vision board deleted successfully',
    });
    loadVisionBoards();
  };

  if (previewBoard) {
    return (
      <div className="space-y-4">
        <Button onClick={() => setPreviewBoard(null)} variant="outline">
          Back to Editor
        </Button>
        <VisionBoardDisplay board={previewBoard} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Create Your Vision Board</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title and Description */}
          <div className="space-y-4">
            <Input
              placeholder="Vision Board Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Image Upload */}
          <div className="space-y-4">
            <label className="block text-sm font-medium">
              Vision Board Images
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((img, index) => (
                <div key={index} className="relative group">
                  <img
                    src={img.url}
                    alt={`Vision ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Input
                    placeholder="Caption (optional)"
                    value={img.caption || ''}
                    onChange={(e) => updateImageCaption(index, e.target.value)}
                    className="mt-2"
                  />
                </div>
              ))}
              <label className="flex items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Upload className="h-8 w-8 text-muted-foreground" />
              </label>
            </div>
          </div>

          {/* Affirmations */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Affirmations</label>
              <Button size="sm" variant="outline" onClick={addAffirmation}>
                <Plus className="h-4 w-4 mr-2" />
                Add Affirmation
              </Button>
            </div>
            {affirmations.map((affirmation, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder="I am manifesting..."
                  value={affirmation}
                  onChange={(e) => updateAffirmation(index, e.target.value)}
                />
                {affirmations.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeAffirmation(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Action Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Action Steps</label>
              <Button size="sm" variant="outline" onClick={addActionItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Action
              </Button>
            </div>
            {actionItems.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder="Action to take..."
                  value={item.text}
                  onChange={(e) => updateActionItem(index, e.target.value)}
                />
                {actionItems.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeActionItem(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button onClick={handlePreview} variant="outline">
              <Play className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button onClick={saveVisionBoard} disabled={loading}>
              {loading ? 'Saving...' : 'Save Vision Board'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Saved Vision Boards */}
      {savedBoards.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Your Vision Boards</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedBoards.map((board) => (
                <Card key={board.id} className="overflow-hidden">
                  <div className="relative h-48">
                    {board.images[0] && (
                      <img
                        src={board.images[0].url}
                        alt={board.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2">{board.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {board.affirmations.length} affirmations • {board.action_items.length} actions
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewBoard(board)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteBoard(board.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
